/**
 * AuditConfigSection — Settings page for audit configuration
 *
 * Allows workspace admins to configure the minimum completion percentage
 * threshold for audit readiness.
 */

import { useState, useEffect } from "react";
import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import {
  SettingsPageHeader,
  SettingsPanel,
  SettingsField,
  SettingsActionBar,
} from "./SettingsSection";
import { Button } from "@/components/ui";
import { Loader2 } from "lucide-react";

export function AuditConfigSection() {
  const { t } = useTranslation(["settings", "common"]);
  const workspaceId = useWorkspaceId();
  const { data: settings, isLoading } = useWorkspaceQuery(
    api.workspaces.getWorkspaceSettings,
  );

  const updateSettings = useApiMutation(
    api.workspaces.updateWorkspaceSettings,
    {
      name: "update-workspace-settings",
    },
  );

  const [threshold, setThreshold] = useState<number>(90);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings) {
      setThreshold(settings.minCompletionPercent);
      setHasChanges(false);
    }
  }, [settings]);

  const handleChange = (value: number) => {
    const clamped = Math.max(0, Math.min(100, value));
    setThreshold(clamped);
    setHasChanges(clamped !== (settings?.minCompletionPercent ?? 90));
  };

  const handleSave = async () => {
    await updateSettings.mutateAsync({
      workspaceId,
      minCompletionPercent: threshold,
    });
    setHasChanges(false);
  };

  const handleReset = () => {
    if (settings) {
      setThreshold(settings.minCompletionPercent);
      setHasChanges(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-muted" size={20} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <SettingsPageHeader
        eyebrow={t("settings:sections.organization")}
        title={t("settings:auditConfig.title")}
        description={t("settings:auditConfig.description")}
      />

      <SettingsPanel
        title={t("settings:auditConfig.completionThreshold.title")}
        description={t("settings:auditConfig.completionThreshold.description")}
        footer={
          <SettingsActionBar
            hint={
              hasChanges
                ? t("settings:auditConfig.unsavedChanges")
                : t("settings:auditConfig.noChanges")
            }
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReset}
              disabled={!hasChanges}
            >
              {t("common:actions.reset")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || updateSettings.isPending}
            >
              {updateSettings.isPending ? (
                <Loader2 className="animate-spin mr-1.5" size={14} />
              ) : null}
              {t("common:actions.save")}
            </Button>
          </SettingsActionBar>
        }
      >
        <SettingsField
          label={t("settings:auditConfig.completionThreshold.label")}
          description={t("settings:auditConfig.completionThreshold.help")}
        >
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={threshold}
              onChange={(e) => handleChange(Number(e.target.value))}
              className="flex-1 accent-accent h-2"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                max={100}
                value={threshold}
                onChange={(e) => handleChange(Number(e.target.value))}
                className="w-16 rounded-lg border border-border/80 bg-white px-2.5 py-1.5 text-center text-sm font-medium text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30"
              />
              <span className="text-sm text-muted">%</span>
            </div>
          </div>
        </SettingsField>
      </SettingsPanel>
    </div>
  );
}
